import React, { useState } from 'react';
import { View, Pressable, TextInput } from 'react-native-web';
import { useNavigate } from 'react-router-dom';
import CustomText from '../../components/atoms/CustomText';
import Button from '../../components/common/Button';
import { colors, spacing } from '../../utils/styling';
import { useResponsive } from '../../hooks/useResponsive';
import {
  useAdvisorMe,
  useAdvisors,
  useRoutingRules,
  useCreateRoutingRule,
  useDeleteRoutingRule,
  useAdvisorLeads,
  useMarkLeadHandled,
} from '../../lib/advisors';

// Item 5 (ClickUp 86badfku3) — per-firm lead routing config + accountability.
// McKayla 2026-06-18: routing thresholds are NOT hardcoded tiers; each firm sets
// their own at account setup ("some firms may have 1 advisor / different net-worth
// standards"). A firm_admin configures asset-tier -> advisor rules here; new
// attributed/walk-in leads auto-assign by the matching rule (backend evaluate_routing).
// Plus the "N unanswered" accountability view.

const fmtMoney = (n: number | null | undefined) =>
  n === null || n === undefined ? 'Any' : `$${Number(n).toLocaleString()}`;

export default function RoutingConfig() {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  const { data: me, isLoading: meLoading, isError: notAdvisor } = useAdvisorMe();
  const isAdmin = me?.role === 'firm_admin';

  const { data: advisors } = useAdvisors({ enabled: isAdmin });
  const { data: rules, isLoading: rulesLoading } = useRoutingRules({ enabled: !!me });
  const { data: unhandled, isError: unhandledError } = useAdvisorLeads({ unhandledOnly: true, enabled: !!me });

  const createRule = useCreateRoutingRule();
  const deleteRule = useDeleteRoutingRule();
  const markHandled = useMarkLeadHandled();

  // New-rule form state
  const [advisorId, setAdvisorId] = useState<string>('');
  const [minAssets, setMinAssets] = useState('');
  const [maxAssets, setMaxAssets] = useState('');
  const [priority, setPriority] = useState('100');
  const [formError, setFormError] = useState('');

  const advisorName = (id: number) => {
    const a = advisors?.find((x) => x.id === id);
    return a ? `Advisor #${a.id}${a.role === 'firm_admin' ? ' (admin)' : ''}` : `Advisor #${id}`;
  };

  const handleAddRule = async () => {
    setFormError('');
    if (!advisorId) {
      setFormError('Pick an advisor for this rule.');
      return;
    }
    const min = minAssets.trim() === '' ? null : Number(minAssets);
    const max = maxAssets.trim() === '' ? null : Number(maxAssets);
    if (min !== null && Number.isNaN(min)) { setFormError('Min assets must be a number.'); return; }
    if (max !== null && Number.isNaN(max)) { setFormError('Max assets must be a number.'); return; }
    // Blank priority -> default 100; a non-numeric entry is an error; a valid 0 stays 0.
    const prio = priority.trim() === '' ? 100 : Number(priority);
    if (Number.isNaN(prio)) { setFormError('Priority must be a number.'); return; }
    try {
      await createRule.mutateAsync({
        advisor_id: Number(advisorId),
        min_assets: min,
        max_assets: max,
        priority: prio,
      });
      setAdvisorId(''); setMinAssets(''); setMaxAssets(''); setPriority('100');
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to add rule.');
    }
  };

  if (meLoading) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CustomText style={{ fontFamily: 'Nunito' }}>Loading…</CustomText>
      </View>
    );
  }

  if (notAdvisor || !me) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: spacing.lg }}>
        <CustomText style={{ fontFamily: 'Nunito', fontSize: 18, color: colors.dark, marginBottom: spacing.md }}>
          This area is for financial advisors.
        </CustomText>
        <Button label="Back to portal" onPress={() => navigate('/advisor')} rounded={false} />
      </View>
    );
  }

  const selectStyle: React.CSSProperties = {
    height: 38, borderRadius: 6, border: `1px solid ${colors.gray[300]}`, padding: '0 8px',
    fontFamily: 'Nunito', fontSize: 14, backgroundColor: colors.white, color: colors.dark, cursor: 'pointer',
  };
  const inputStyle = {
    height: 38, borderWidth: 1, borderColor: colors.gray[200], borderRadius: 6, paddingHorizontal: 10,
    outlineStyle: 'none' as const, fontFamily: 'Nunito', fontSize: 14, backgroundColor: colors.white, width: '100%',
  };

  return (
    <View style={{ flex: 1, minHeight: '100vh', backgroundColor: colors.gray[100], alignItems: 'center', padding: spacing.lg, overflowY: 'auto' }}>
      <View style={{ width: '100%', maxWidth: 880, paddingVertical: spacing.lg }}>
        <Pressable onPress={() => navigate('/advisor')} style={{ marginBottom: spacing.md }}>
          <CustomText style={{ fontFamily: 'Nunito', fontSize: 14, color: colors.primary, textDecorationLine: 'underline' }}>← Back to portal</CustomText>
        </Pressable>

        <CustomText style={{ fontFamily: 'Nunito', fontSize: isMobile ? 26 : 34, fontWeight: '400', color: colors.dark, marginBottom: spacing.xs }}>
          Lead routing
        </CustomText>
        <CustomText style={{ fontFamily: 'Nunito', fontSize: 15, color: colors.gray[600], marginBottom: spacing.xl }}>
          Set how new leads route to advisors by asset level. Leave the minimum blank for a catch-all rule. Rules are evaluated in priority order (lowest first).
        </CustomText>

        {/* Accountability — unhandled leads */}
        <View style={{ backgroundColor: colors.white, borderRadius: 12, padding: spacing.lg, marginBottom: spacing.xl, borderWidth: 1, borderColor: colors.gray[200] }}>
          <CustomText style={{ fontFamily: 'Nunito', fontSize: 18, fontWeight: '600', color: colors.dark, marginBottom: spacing.sm }}>
            Unanswered leads {unhandled ? `(${unhandled.length})` : ''}
          </CustomText>
          {unhandledError ? (
            <CustomText style={{ fontFamily: 'Nunito', fontSize: 14, color: colors.danger }}>Couldn't load leads right now — please refresh.</CustomText>
          ) : !unhandled || unhandled.length === 0 ? (
            <CustomText style={{ fontFamily: 'Nunito', fontSize: 14, color: colors.gray[600] }}>Nothing waiting — you're all caught up.</CustomText>
          ) : (
            unhandled.map((lead) => (
              <View key={lead.id} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.gray[200] }}>
                <View style={{ flex: 1 }}>
                  <CustomText style={{ fontFamily: 'Nunito', fontSize: 14, color: colors.dark }}>{lead.first_name} {lead.last_name}</CustomText>
                  <CustomText style={{ fontFamily: 'Nunito', fontSize: 12, color: colors.gray[600] }}>
                    {lead.email} · Net worth {fmtMoney(lead.net_worth)} · {lead.source || 'walk_in'}
                  </CustomText>
                </View>
                <Pressable
                  onPress={() => markHandled.mutate(lead.id)}
                  style={{ paddingHorizontal: spacing.md, paddingVertical: spacing.xs, borderRadius: 8, borderWidth: 1, borderColor: colors.primary, cursor: 'pointer' }}
                >
                  <CustomText style={{ fontFamily: 'Nunito', fontSize: 13, color: colors.primary }}>Mark handled</CustomText>
                </Pressable>
              </View>
            ))
          )}
        </View>

        {/* Existing rules */}
        <View style={{ backgroundColor: colors.white, borderRadius: 12, padding: spacing.lg, marginBottom: spacing.lg, borderWidth: 1, borderColor: colors.gray[200] }}>
          <CustomText style={{ fontFamily: 'Nunito', fontSize: 18, fontWeight: '600', color: colors.dark, marginBottom: spacing.md }}>
            Routing rules
          </CustomText>
          {rulesLoading ? (
            <CustomText style={{ fontFamily: 'Nunito', fontSize: 14, color: colors.gray[600] }}>Loading…</CustomText>
          ) : !rules || rules.length === 0 ? (
            <CustomText style={{ fontFamily: 'Nunito', fontSize: 14, color: colors.gray[600] }}>
              No rules yet. {isAdmin ? 'Add one below.' : 'Ask your firm admin to set these up.'}
            </CustomText>
          ) : (
            rules.map((r) => (
              <View key={r.id} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.gray[200] }}>
                <CustomText style={{ fontFamily: 'Nunito', fontSize: 14, color: colors.dark, flex: 1 }}>
                  {fmtMoney(r.min_assets)} – {fmtMoney(r.max_assets)} → {advisorName(r.advisor_id)}  ·  priority {r.priority}
                </CustomText>
                {isAdmin ? (
                  <Pressable onPress={() => deleteRule.mutate(r.id)} style={{ paddingHorizontal: spacing.sm, cursor: 'pointer' }}>
                    <CustomText style={{ fontFamily: 'Nunito', fontSize: 13, color: colors.danger }}>Remove</CustomText>
                  </Pressable>
                ) : null}
              </View>
            ))
          )}
        </View>

        {/* Add-rule form (firm admins only) */}
        {isAdmin ? (
          <View style={{ backgroundColor: colors.white, borderRadius: 12, padding: spacing.lg, borderWidth: 1, borderColor: colors.gray[200] }}>
            <CustomText style={{ fontFamily: 'Nunito', fontSize: 18, fontWeight: '600', color: colors.dark, marginBottom: spacing.md }}>
              Add a rule
            </CustomText>
            <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: spacing.md, marginBottom: spacing.md, alignItems: isMobile ? 'stretch' : 'flex-end' }}>
              <View style={{ flex: 1 }}>
                <CustomText style={{ fontFamily: 'Nunito', fontSize: 12, color: colors.dark, marginBottom: spacing.sm }}>Advisor</CustomText>
                <select value={advisorId} onChange={(e) => setAdvisorId(e.target.value)} style={selectStyle}>
                  <option value="">Select…</option>
                  {(advisors || []).map((a) => (
                    <option key={a.id} value={a.id}>{advisorName(a.id)}</option>
                  ))}
                </select>
              </View>
              <View style={{ flex: 1 }}>
                <CustomText style={{ fontFamily: 'Nunito', fontSize: 12, color: colors.dark, marginBottom: spacing.sm }}>Min assets (blank = catch-all)</CustomText>
                <TextInput value={minAssets} onChangeText={setMinAssets} placeholder="e.g. 5000000" placeholderTextColor={colors.gray[500]} style={inputStyle} />
              </View>
              <View style={{ flex: 1 }}>
                <CustomText style={{ fontFamily: 'Nunito', fontSize: 12, color: colors.dark, marginBottom: spacing.sm }}>Max assets (optional)</CustomText>
                <TextInput value={maxAssets} onChangeText={setMaxAssets} placeholder="(none)" placeholderTextColor={colors.gray[500]} style={inputStyle} />
              </View>
              <View style={{ width: isMobile ? '100%' : 90 }}>
                <CustomText style={{ fontFamily: 'Nunito', fontSize: 12, color: colors.dark, marginBottom: spacing.sm }}>Priority</CustomText>
                <TextInput value={priority} onChangeText={setPriority} placeholder="100" placeholderTextColor={colors.gray[500]} style={inputStyle} />
              </View>
            </View>
            {formError ? <CustomText style={{ color: colors.danger, fontFamily: 'Nunito', fontSize: 13, marginBottom: spacing.sm }}>{formError}</CustomText> : null}
            <Button label="Add rule" onPress={handleAddRule} loading={createRule.isPending} disabled={createRule.isPending} rounded={false} style={{ height: 40, alignSelf: 'flex-start', paddingHorizontal: spacing.xl }} />
          </View>
        ) : (
          <CustomText style={{ fontFamily: 'Nunito', fontSize: 13, color: colors.gray[600] }}>
            Only a firm admin can change routing rules.
          </CustomText>
        )}
      </View>
    </View>
  );
}
